import { apiClient, fetchWrapper, getRequestHeaders, IBaseResponse } from './apiBase';
import { BASE_API_URL } from '@/config/environment';

import { ISchemaField, IEntityUser } from '@/types';
import { TSchemaDatatype } from '@/lib/dataFrameTypes';
import { ISchemaSummary } from '@/lib/repositorySchemasApi';

export interface ICompareSchemaField {
  name: string;
  left?: ISchemaField;
  right?: ISchemaField;
  elementId?: string;
  // dtype?: TSchemaDatatype;
  dtype: string;
}

export type TAssociationFieldSelection = {
  compare_method?: string | null;
  left?: string | null;
  right?: string | null;
  alias_as?: string | null;
};

type TDisplayFieldSelection = {
  compare_method?: string | null;
  left?: string | null;
  right?: string | null;
  alias_as?: string | null;
};

export type TAssociation = {
  isKey?: boolean;
  isTarget?: boolean;
  left: ICompareSchemaField;
  right: ICompareSchemaField;
};

export type TCompareDefinition = {
  title?: string;
  description?: string;
  data_frames: {
    left: {
      path: string;
      version: string;
    };
    right: {
      path: string;
      version: string;
    };
  };
  fields: {
    key: TAssociationFieldSelection[];
    compare: TAssociationFieldSelection[];
    display: TDisplayFieldSelection[];
  };
};

type TCompareSummary = {
  modifications: {
    added_rows: number;
    removed_rows: number;
    modified_rows: number;
  };
  schema: ISchemaSummary;
};

export type TComparison = {
  compare_number: number;
  created_by: IEntityUser;
  definition: TCompareDefinition;
  derived_data_frame_url: string;
  inserted_at: string;
  updated_at: string;
  summary: TCompareSummary;
};

interface ICreateCompareParams {
  namespace: string;
  repositoryName: string;
  token?: string;
  payload: TCompareDefinition;
}

export interface IUpdateCompareParams extends ICreateCompareParams {
  compareId: string;
}

interface IDeleteCompareParams {
  namespace: string;
  repositoryName: string;
  token?: string;
  compareId: string;
}

type SchemaDiffField = {
  dtype: string;
  name: string;
  key: string;
};

type TSchemaDiff = {
  added_cols: SchemaDiffField[];
  removed_cols: SchemaDiffField[];
};

export type TCompareMessage = {
  title: string;
  description: string;
  level: string;
};

export type ISourceDataFrame = {
  path: string;
  version: string;
  schema: ISchemaSummary;
};

export interface ITabularCompare {
  compare: TComparison;
  repository: {
    id: string;
    namespace: string;
    name: string;
  };
  source_data_frames: {
    left: ISourceDataFrame;
    right: ISourceDataFrame;
  };
  messages?: TCompareMessage[];
  schema_diff: TSchemaDiff;
  summary: TCompareSummary;
}

export interface ICompareResponse extends IBaseResponse, ITabularCompare {}

interface ICompareListResponse extends IBaseResponse {
  compares: TComparison[];
}

export const updateCompare = async ({
  compareId,
  namespace,
  repositoryName,
  token,
  payload,
}: IUpdateCompareParams): Promise<ICompareResponse> => {
  const requestHeaders = getRequestHeaders(token);
  const targetUrl = `${BASE_API_URL}/repos/${namespace}/${repositoryName}/compares/${compareId}`;
  try {
    const res = await apiClient.put(targetUrl, payload, { headers: requestHeaders });
    return res.data;
  } catch (error: any) {
    return error.response.data;
  }
};

export const deleteCompare = async ({
  compareId,
  namespace,
  repositoryName,
  token,
}: IDeleteCompareParams): Promise<ICompareResponse> => {
  const requestHeaders = getRequestHeaders(token);
  const targetUrl = `${BASE_API_URL}/repos/${namespace}/${repositoryName}/compares/${compareId}`;
  try {
    const res = await apiClient.delete(targetUrl, { headers: requestHeaders });
    return res.data;
  } catch (error: any) {
    return error.response.data;
  }
};

export const createCompare = async ({
  namespace,
  repositoryName,
  token,
  payload,
}: ICreateCompareParams): Promise<ICompareResponse> => {
  const requestHeaders = getRequestHeaders(token);
  const targetUrl = `${BASE_API_URL}/repos/${namespace}/${repositoryName}/compares`;
  try {
    const res = await apiClient.post(targetUrl, payload, { headers: requestHeaders });
    return res.data;
  } catch (error: any) {
    return error.response.data;
  }
};

export const fetchCompare = async (
  namespace: string,
  repositoryName: string,
  compareId: string,
  token?: string,
): Promise<ICompareResponse> => {
  const targetUrl = `${BASE_API_URL}/repos/${namespace}/${repositoryName}/compares/${compareId}`;

  // console.log('fetchCompare > targetUrl', targetUrl);

  return await fetchWrapper({
    resourceName: 'tabularCompare',
    targetUrl,
    authToken: token,
    resourceContext: { namespace, repositoryName, compareId },
  });
};

export const fetchCompareList = async (
  namespace: string,
  repositoryName: string,
  token?: string,
): Promise<ICompareListResponse> => {
  const targetUrl = `${BASE_API_URL}/repos/${namespace}/${repositoryName}/compares?page_size=100`;

  return await fetchWrapper({
    resourceName: 'tabularCompares',
    targetUrl,
    authToken: token,
    resourceContext: { namespace, repositoryName },
  });
};
